import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { claimsApi, patientsApi, payersApi, branchesApi, abdmApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, FloppyDisk, Fingerprint, CheckCircle, Warning as WarningIcon } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const NewClaimPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [payers, setPayers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [abhaInfo, setAbhaInfo] = useState(null);
  const [abhaInput, setAbhaInput] = useState('');
  const [abhaLinking, setAbhaLinking] = useState(false);
  
  const [formData, setFormData] = useState({
    patient_id: '',
    payer_id: '',
    branch_id: '',
    admission_date: new Date().toISOString().split('T')[0],
    discharge_date: '',
    department: '',
    ward: '',
    room_number: '',
    diagnosis: '',
    procedure: '',
    estimated_amount: '',
    claim_type: 'cashless',
    insurance_claim_no: '',
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (patientSearch) {
        searchPatients();
      }
    }, 300);
    return () => clearTimeout(delaySearch);
  }, [patientSearch]);

  const loadInitialData = async () => {
    try {
      const [payersRes, branchesRes] = await Promise.all([
        payersApi.listActive(),
        branchesApi.list()
      ]);
      setPayers(payersRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const searchPatients = async () => {
    try {
      const res = await patientsApi.list(patientSearch);
      setPatients(res.data);
    } catch (error) {
      console.error('Failed to search patients:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'patient_id' && value) {
      fetchAbhaInfo(value);
    } else if (field === 'patient_id') {
      setAbhaInfo(null);
      setAbhaInput('');
    }
  };

  const fetchAbhaInfo = async (patientId) => {
    try {
      const res = await abdmApi.getPatientAbha(patientId);
      setAbhaInfo(res.data);
    } catch {
      setAbhaInfo(null);
    }
  };

  const handleLinkAbha = async () => {
    if (!abhaInput || !formData.patient_id) return;
    setAbhaLinking(true);
    try {
      await abdmApi.linkAbha({ patient_id: formData.patient_id, abha_number: abhaInput });
      toast.success('ABHA linked successfully');
      setAbhaInput('');
      fetchAbhaInfo(formData.patient_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to link ABHA');
    } finally {
      setAbhaLinking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        estimated_amount: parseFloat(formData.estimated_amount) || 0
      };
      
      await claimsApi.create(payload);
      toast.success('Claim created successfully!');
      navigate('/claims');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create claim');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="new-claim-page">
      <Toaster position="top-right" />
      
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/claims')}
          className="text-[#595959]"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-light font-['Outfit'] text-[#1C1C1C]">New Case</h1>
          <p className="text-[#595959] mt-1">Register a new insurance claim</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Patient Information */}
          <Card className="bg-white border-[#E5E5E5]">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit'] text-[#1C1C1C]">Patient Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Search Patient</Label>
                <Input
                  placeholder="Search by name, UHID, or phone..."
                  value={patientSearch}
                  onChange={(e) => setPatientSearch(e.target.value)}
                  className="border-[#E5E5E5]"
                  data-testid="patient-search-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Select Patient *</Label>
                <Select 
                  value={formData.patient_id} 
                  onValueChange={(v) => handleChange('patient_id', v)}
                  required
                >
                  <SelectTrigger className="border-[#E5E5E5]" data-testid="patient-select">
                    <SelectValue placeholder="Select a patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map(patient => (
                      <SelectItem key={patient.id} value={patient.id}>
                        {patient.name} - {patient.uhid}
                      </SelectItem>
                    ))}
                    {patients.length === 0 && (
                      <div className="p-2 text-sm text-[#595959] text-center">
                        {patientSearch ? 'No patients found' : 'Search for a patient'}
                      </div>
                    )}
                  </SelectContent>
                </Select>
                <Button 
                  type="button" 
                  variant="link" 
                  className="p-0 h-auto text-[#2D4A3E]"
                  onClick={() => navigate('/patients/new?redirect=/claims/new')}
                >
                  + Add New Patient
                </Button>
              </div>

              {/* ABHA Identity Section */}
              {formData.patient_id && (
                <div className="border border-teal-200 rounded-lg p-3 bg-teal-50/50 space-y-2" data-testid="abha-section">
                  <div className="flex items-center gap-2">
                    <Fingerprint className="h-4 w-4 text-teal-600" />
                    <span className="text-xs font-semibold text-teal-800 uppercase tracking-wider">ABHA Identity</span>
                  </div>
                  {abhaInfo?.linked ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-700">{abhaInfo.abha_masked}</span>
                      {abhaInfo.verification_status === 'verified' ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full" data-testid="abha-verified-badge">
                          <CheckCircle className="h-3 w-3" weight="fill" /> Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full" data-testid="abha-pending-badge">
                          <WarningIcon className="h-3 w-3" /> Pending
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-slate-500">No ABHA linked. Enter ABHA to link (XX-XXXX-XXXX-XXXX):</p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="XX-XXXX-XXXX-XXXX"
                          value={abhaInput}
                          onChange={e => setAbhaInput(e.target.value)}
                          className="border-teal-200 text-sm h-8"
                          data-testid="abha-input"
                        />
                        <Button type="button" size="sm" className="h-8 bg-teal-600 text-white hover:bg-teal-700 text-xs"
                          onClick={handleLinkAbha} disabled={abhaLinking || !abhaInput} data-testid="link-abha-btn">
                          {abhaLinking ? 'Linking...' : 'Link'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payer & Insurance */}
          <Card className="bg-white border-[#E5E5E5]">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit'] text-[#1C1C1C]">Payer & Insurance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Payer/TPA *</Label>
                <Select 
                  value={formData.payer_id} 
                  onValueChange={(v) => handleChange('payer_id', v)}
                  required
                >
                  <SelectTrigger className="border-[#E5E5E5]" data-testid="payer-select">
                    <SelectValue placeholder="Select payer" />
                  </SelectTrigger>
                  <SelectContent>
                    {payers.map(payer => (
                      <SelectItem key={payer.id} value={payer.id}>
                        {payer.name} ({payer.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {payers.length === 0 && (
                  <Button 
                    type="button" 
                    variant="link" 
                    className="p-0 h-auto text-[#2D4A3E]"
                    onClick={() => navigate('/payers')}
                  >
                    + Add Payer First
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                <Label>Claim Type</Label>
                <Select value={formData.claim_type} onValueChange={(v) => handleChange('claim_type', v)}>
                  <SelectTrigger className="border-[#E5E5E5]" data-testid="claim-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cashless">Cashless</SelectItem>
                    <SelectItem value="reimbursement">Reimbursement</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Claim No (from Insurance)</Label>
                <Input
                  placeholder="Claim number received from insurance company"
                  value={formData.insurance_claim_no}
                  onChange={(e) => handleChange('insurance_claim_no', e.target.value)}
                  className="border-[#E5E5E5]"
                  data-testid="insurance-claim-no-input"
                />
                <p className="text-[10px] text-slate-400">Optional. Enter the claim/reference number provided by the insurance company or TPA.</p>
              </div>

              <div className="space-y-2">
                <Label>Estimated Amount (₹)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={formData.estimated_amount}
                  onChange={(e) => handleChange('estimated_amount', e.target.value)}
                  className="border-[#E5E5E5]"
                  data-testid="estimated-amount-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Admission Details */}
          <Card className="bg-white border-[#E5E5E5]">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit'] text-[#1C1C1C]">Admission Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Admission Date *</Label>
                  <Input
                    type="date"
                    value={formData.admission_date}
                    onChange={(e) => handleChange('admission_date', e.target.value)}
                    className="border-[#E5E5E5]"
                    required
                    data-testid="admission-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Discharge Date</Label>
                  <Input
                    type="date"
                    value={formData.discharge_date}
                    onChange={(e) => handleChange('discharge_date', e.target.value)}
                    className="border-[#E5E5E5]"
                    data-testid="discharge-date-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Branch</Label>
                <Select value={formData.branch_id} onValueChange={(v) => handleChange('branch_id', v)}>
                  <SelectTrigger className="border-[#E5E5E5]" data-testid="branch-select">
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Input
                    placeholder="e.g., Cardiology"
                    value={formData.department}
                    onChange={(e) => handleChange('department', e.target.value)}
                    className="border-[#E5E5E5]"
                    data-testid="department-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ward</Label>
                  <Input
                    placeholder="e.g., ICU"
                    value={formData.ward}
                    onChange={(e) => handleChange('ward', e.target.value)}
                    className="border-[#E5E5E5]"
                    data-testid="ward-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Room #</Label>
                  <Input
                    placeholder="e.g., 101"
                    value={formData.room_number}
                    onChange={(e) => handleChange('room_number', e.target.value)}
                    className="border-[#E5E5E5]"
                    data-testid="room-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Clinical Information */}
          <Card className="bg-white border-[#E5E5E5]">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit'] text-[#1C1C1C]">Clinical Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Diagnosis</Label>
                <Textarea
                  placeholder="Primary diagnosis..."
                  value={formData.diagnosis}
                  onChange={(e) => handleChange('diagnosis', e.target.value)}
                  className="border-[#E5E5E5] min-h-[80px]"
                  data-testid="diagnosis-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Procedure</Label>
                <Textarea
                  placeholder="Procedures performed..."
                  value={formData.procedure}
                  onChange={(e) => handleChange('procedure', e.target.value)}
                  className="border-[#E5E5E5] min-h-[80px]"
                  data-testid="procedure-input"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-4 mt-6">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate('/claims')}
            className="border-[#E5E5E5]"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            className="bg-[#2D4A3E] hover:bg-[#1F362C] text-white"
            disabled={loading || !formData.patient_id || !formData.payer_id}
            data-testid="submit-claim-btn"
          >
            <FloppyDisk className="h-4 w-4 mr-2" />
            {loading ? 'Creating...' : 'Create Claim'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NewClaimPage;
